import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Heart } from 'lucide-react'
import { useCurrentUser } from '@/shared/api/currentUser'
import { useFavoriteIds, useToggleFavorite } from '@/shared/api/favoritesQueries'
import { cn } from '@/shared/lib/utils'

/**
 * Heart toggle for a property. Hidden for guests. `overlay` sits on top of a card image;
 * `chip` renders inline next to other badges.
 */
export function FavoriteHeartButtonView({
  propertyId,
  variant = 'chip',
  className,
  isFavorite: isFavoriteProp,
}: {
  propertyId: string
  variant?: 'overlay' | 'chip'
  className?: string
  /** When known (e.g. favorites list page), force favorited UI even before ids query resolves. */
  isFavorite?: boolean
}) {
  const { t } = useTranslation()
  const { data: currentUser } = useCurrentUser()
  const { data: favoriteIds } = useFavoriteIds()
  const toggle = useToggleFavorite()

  if (!currentUser?.id) {
    return null
  }

  if (isFavoriteProp === undefined && favoriteIds === undefined) {
    return null
  }

  const isFavorite = isFavoriteProp === true || (favoriteIds?.has(propertyId) ?? false)

  // The card is a link; keep the toggle from navigating or bubbling to it.
  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    toggle.mutate({ propertyId, isFavorite })
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={toggle.isPending}
      aria-pressed={isFavorite}
      aria-label={isFavorite ? t('favorites.remove') : t('favorites.add')}
      className={cn(
        'inline-flex items-center justify-center rounded-pill transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
        variant === 'overlay'
          ? 'absolute left-2 top-2 size-9 bg-card/90 text-primary shadow-sm hover:bg-card'
          : 'min-h-7 gap-1.5 border px-2.5 text-xs font-medium text-primary hover:bg-muted/50',
        className,
      )}
    >
      <Heart className={cn('size-4 shrink-0', isFavorite && 'fill-current')} aria-hidden />
      {variant === 'chip' ? (
        <span>{isFavorite ? t('favorites.saved_label') : t('favorites.add_label')}</span>
      ) : null}
    </button>
  )
}

export type FavoriteHeartButtonProps = {
  propertyId: string
  variant?: 'overlay' | 'chip'
  className?: string
  isFavorite?: boolean
}
